/**
 * The one place that knows what a customer credential looks like.
 *
 * It lives in the shared package, and in the browser-safe entry point, because
 * both sides of the Nexus assistant need it: the composer warns the user before
 * their message leaves the browser, and rest-api redacts the same message again
 * at the server boundary (PRE-3499). Two matchers would drift, and a drifted
 * matcher is one that stops catching what the other still does.
 *
 * It is pure string work — no Node built-ins — so the front end can import it.
 */

/** What Cloudflare and Prerender both draw a token body from. */
const TOKEN_CHARS = 'A-Za-z0-9_-';

/**
 * Cloudflare's prefixed tokens: a `cf` prefix, up to three more lower-case
 * letters naming the kind, an underscore, then a long body.
 *
 * Two deliberate widenings against the form this file shipped with:
 *
 *  - **the body class includes `-` and `_`**, because a real token contains
 *    them. It did not before, and the effect was worse than a miss:
 *    `cfut_AAA…-SECRET` was redacted up to the dash, the tail was sent, and the
 *    user was shown a banner saying their token had been removed;
 *  - **the prefix is `cf[a-z]{0,3}_`, not `cfut?_`**, which makes this pattern a
 *    strict superset of the one `buildNexusSummary.ts` carries. Two matchers
 *    drift, and the drifted one stops catching what the other still does; the
 *    superset is what lets that one be deleted in favour of this one without
 *    losing a case it caught.
 *
 * Exported without the `g` flag so a caller can `.test()` it without inheriting
 * a moving `lastIndex`.
 */
export const CF_TOKEN_PATTERN = new RegExp(`cf[a-z]{0,3}_[${TOKEN_CHARS}]{20,}`);

/** The length Cloudflare issues an unprefixed API token at. */
const BARE_TOKEN_LENGTH = 40;

/**
 * The unprefixed Cloudflare API token: exactly 40 characters, no prefix to
 * key on. Three conditions keep it off ordinary text.
 *
 * 1. **The run is maximal and exactly 40 long.** `(^|[^…])` on the left and
 *    `(?![…])` on the right mean a longer run does not match a 40-character
 *    window inside itself. `prerender-worker-023e105f4ecef8ad9ca31a8372d0c353`
 *    is 49 characters, so it is not a token, and a 32-character zone id is not
 *    one either.
 * 2. **A lowercase letter, an uppercase letter and a digit, all three.** Each
 *    one buys a specific exclusion, and all three were needed:
 *      - a git commit SHA is 40 single-case hex characters, so requiring both
 *        cases drops it. It is the one 40-character identifier that really does
 *        turn up in a support conversation;
 *      - `getCloudflareIntegrationFlowByDomainName` is exactly 40 mixed-case
 *        characters. A pasted symbol name has no digit in it, so requiring a
 *        digit drops that whole class. This one was found by the test below,
 *        not by reasoning, which is why the test is there.
 *    The false negative this buys is measurable rather than guessed. A token is
 *    drawn at random from at least 62 characters, so the chance it is missing a
 *    digit is (52/62)^40, about 1 in 1100; missing a whole case class is under
 *    1e-9. So the digit is the only one of the three with a cost worth naming,
 *    and one token in 1100 is the price of not redacting every long symbol name
 *    a customer pastes. It is also not the only control: the caller passes the
 *    exact values it holds (see `known` below), and rest-api runs the same
 *    matcher again at its own boundary.
 * 3. A leading delimiter is captured, not consumed, so the replacement can put
 *    it back. A lookbehind would read better; Safari only got them in 16.4 and
 *    this file runs in the browser.
 *
 * What it still over-matches is a 40-character mixed-case base64 or base64url
 * run that happens to carry a digit — a chunk of an opaque URL parameter, say.
 * That trade is taken deliberately: over-matching puts a visible `[redacted]`
 * in a message the user can rephrase, and under-matching puts a live credential
 * in a LangGraph thread we do not control and cannot purge.
 */
const BARE_TOKEN_SOURCE =
  `(^|[^${TOKEN_CHARS}])` +
  `(?=[${TOKEN_CHARS}]{${BARE_TOKEN_LENGTH}}(?![${TOKEN_CHARS}]))` +
  `(?=[${TOKEN_CHARS}]*[a-z])` +
  `(?=[${TOKEN_CHARS}]*[A-Z])` +
  `(?=[${TOKEN_CHARS}]*[0-9])` +
  `[${TOKEN_CHARS}]{${BARE_TOKEN_LENGTH}}`;

/** The shortest value worth removing by exact match. Below this it is not a secret. */
const MIN_KNOWN_LENGTH = 8;

const REDACTED = '[redacted]';

/**
 * A fresh global expression for each call.
 *
 * A shared one with the `g` flag keeps its `lastIndex` between calls, so the
 * second call on the same input answers `false`.
 */
function everyPrefixedToken(): RegExp {
  return new RegExp(CF_TOKEN_PATTERN.source, 'g');
}

function everyBareToken(): RegExp {
  return new RegExp(BARE_TOKEN_SOURCE, 'g');
}

/** True when `text` contains something shaped like a credential. */
export function containsCredential(text: string): boolean {
  return everyPrefixedToken().test(text) || everyBareToken().test(text);
}

/**
 * The known values to remove, longest first.
 *
 * Longest first matters: if one known value is a prefix of another, removing
 * the short one first leaves the tail of the long one in the text.
 */
function knownValues(known: string | readonly string[] | undefined): string[] {
  const list = known === undefined ? [] : typeof known === 'string' ? [known] : known;

  // `filter` already returns a new array, so sorting it in place cannot reach
  // the caller's.
  return list.filter((value) => value.length >= MIN_KNOWN_LENGTH).sort((a, b) => b.length - a.length);
}

/**
 * Replaces every credential-shaped run with `[redacted]`.
 *
 * Known values are removed with `split`/`join`, not by building an expression
 * from them: a value that happened to contain regular-expression syntax would
 * otherwise change what the matcher matches.
 *
 * `known` is for the credentials shape cannot reach. A Prerender API token is
 * 20 mixed-case alphanumeric characters, which is also the shape of a great
 * many function names, so no pattern can claim it — but the caller is holding
 * the exact value (`user.api_token` on the server, `prerenderUser.token` in the
 * browser) and an exact match has no false positives at all. It takes a list
 * because a message can carry more than one: the Cloudflare token and the
 * Prerender token are different values of different shapes.
 */
export function redactCredentials(text: string, known?: string | readonly string[]): string {
  const withoutKnown = knownValues(known).reduce((carry, value) => carry.split(value).join(REDACTED), text);

  return withoutKnown.replace(everyPrefixedToken(), REDACTED).replace(everyBareToken(), `$1${REDACTED}`);
}
